import React, { useState } from "react";
import Swal from "sweetalert2";
import { useSignUpMutation } from "../../api/authApi";
import { encryptData, decryptData } from "../../utils/encryptDecryptData";

const Register = ({ isSignIn, toggleForm }) => {
  const [signUp, { isLoading }] = useSignUpMutation();
  const [formValues, setFormValues] = useState({
    fName: "",
    lName: "",
    organization: "",
    mobileNo: "",
    email: "",
    password: "",
    aiServiceType: "Sampark AI",
    apiKey: ""
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNo") {
      if (value === "" || (value.length <= 10 && /^[0-9\b]+$/.test(value))) {
        setFormValues({
          ...formValues,
          [name]: value,
        });
      }
    } else {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const validate = () => {
    const newErrors = {};
    let passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!formValues.fName) newErrors.fName = "First name is required";
    if (!formValues.organization)
      newErrors.organization = "Organization is required";
    if (!formValues.mobileNo) newErrors.mobileNo = "Mobile number is required";
    else if (!/^\d{10}$/.test(formValues.mobileNo))
      newErrors.mobileNo = "Mobile number must be exactly 10 digits";
    if (!formValues.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formValues.email))
      newErrors.email = "Invalid email address";
    if (!formValues.password) newErrors.password = "Password is required";
    else if (!passwordRegex.test(formValues.password))
      newErrors.password =
        "Password must include 8+ chars, uppercase, lowercase, number, and special char.";
    if(!formValues.apiKey && formValues.aiServiceType == "Open Ai") newErrors.apiKey = "API Key is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        let encryptedData = {
          fName: encryptData(formValues.fName),
          lName: encryptData(formValues.lName),
          organization: encryptData(formValues.organization),
          mobileNo: encryptData(formValues.mobileNo),
          email: encryptData(formValues.email),
          password: encryptData(formValues.password),
          aiServiceType: encryptData(formValues.aiServiceType),
          apiKey: encryptData(formValues.apiKey)
        }
        console.log(encryptedData,formValues);
        const data = await signUp(formValues).unwrap();
        Swal.fire({
          icon: "success",
          text: data.message,
          confirmButtonColor: "#1EB733",
          width: 350,
        }).then(() => {
          setFormValues({
            fName: "",
            lName: "",
            organization: "",
            mobileNo: "",
            email: "",
            password: "",
          });
          if (data.statusCode == "200" && data.status) {
            toggleForm();
          }
        });
      } catch (error) {
        console.error("Error:", error);
        Swal.fire({
          icon: "error",
          title: error?.data?.message || "Failed to signup",
          confirmButtonColor: "#d33",
          width: 350,
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <>
      <div className="flex flex-col justify-center">
        <form className="flex flex-col gap-2 mt-10" onSubmit={handleSubmit}>
          <div className="flex gap-2">
            <div>
              <label
                htmlFor="fName"
                className="flex text-sm font-medium leading-6 text-gray-900"
              >
                First name
              </label>
              <input
                id="fName"
                name="fName"
                type="text"
                value={formValues.fName}
                onChange={handleChange}
                className="block rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
              {errors.fName && (
                <div className="flex text-red-600 text-[10px]">{errors.fName}</div>
              )}
            </div>

            <div>
              <label
                htmlFor="lName"
                className="flex text-sm font-medium leading-6 text-gray-900"
              >
                Last name
              </label>
              <input
                id="lName"
                name="lName"
                type="text"
                value={formValues.lName}
                onChange={handleChange}
                className="block rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
              {errors.lName && (
                <div className="text-red-600 text-[10px]">{errors.lName}</div>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="organization"
              className="flex text-sm font-medium leading-6 text-gray-900"
            >
              Organization
            </label>
            <div>
              <input
                id="organization"
                name="organization"
                type="text"
                value={formValues.organization}
                onChange={handleChange}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
              {errors.organization && (
                <div className="flex text-red-600 text-[10px]">
                  {errors.organization}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <div>
              <label
                htmlFor="mobileNo"
                className="flex text-sm font-medium leading-6 text-gray-900"
              >
                Mobile no
              </label>
              <div>
                <input
                  id="mobileNo"
                  name="mobileNo"
                  type="text"
                  value={formValues.mobileNo}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors.mobileNo && (
                  <div className="text-red-600 text-[10px] flex">
                    {errors.mobileNo}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="flex text-sm font-medium leading-6 text-gray-900"
              >
                Email
              </label>
              <div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formValues.email}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors.email && (
                  <div className="text-red-600 text-[10px]">{errors.email}</div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label
              htmlFor="password"
              className="flex text-sm font-medium leading-6 text-gray-900"
            >
              Password
            </label>

            <div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formValues.password}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                <span
                  className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <i
                    className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
                    aria-hidden="true"
                  ></i>
                </span>
              </div>
              {errors.password && (
                <div className="text-red-600 text-[10px] flex">
                  {errors.password}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="w-full">
              <label
                htmlFor="aiServiceType"
                className="flex text-sm font-medium leading-6 text-gray-900"
              >
                Ai Service
              </label>
              <select
                id="aiServiceType"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-full p-2 cursor-pointer"
                value={formValues.aiServiceType}
                onChange={(e) => setFormValues({
                  ...formValues,
                  aiServiceType: e.target.value,
                  apiKey: "",
                })}
              >
                <option value="" disabled>
                  Select Ai Service
                </option>
                <option value="Sampark AI">
                  Sampark AI
                </option>
                <option value="Open Ai">
                  Open Ai
                </option>
              </select>
            </div>

            {formValues.aiServiceType == "Open Ai" && (<div className="w-full">
              <label
                htmlFor="apiKey"
                className="flex text-sm font-medium leading-6 text-gray-900"
              >
                Api key
              </label>
              <div>
                <input
                  id="apiKey"
                  name="apiKey"
                  type="text"
                  value={formValues.apiKey}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors.apiKey && (
                  <div className="text-red-600 text-[10px] flex">{errors.apiKey}</div>
                )}
              </div>
            </div>)}
          </div>

          <div className="mt-3">
            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-[#1EB733] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-[#1EB733] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Signing up..." : "Sign up"}
            </button>
          </div>
        </form>
      </div>
        <div
          className=" text-gray-900 text-lg cursor-pointer hover:underline decoration-gray-600 text-center mt-3"
          onClick={toggleForm}
        >
          {isSignIn
            ? "Don't have an account? Sign Up"
            : "Already have an account? Sign In"}
        </div>
    </>
  );
};

export default Register;
